<template>
  <div class="rule-waiting-list-content-component flex flex-col gap-4">
    <div v-if="!isRuleSpecific" class="flex flex-col gap-3 sm:flex-row">
      <div class="relative min-w-0 sm:flex-1">
        <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
        <ui-input
          v-model="searchTerm"
          type="search"
          class="pl-9"
          :placeholder="tr('Search') + '...'"
          :aria-label="tr('Search')"
        />
      </div>
      <brand-filter v-model="internalBrandId" class="sm:max-w-64" />
    </div>

    <p v-if="compact && !isLoading && isListEmpty" class="m-0 py-4 text-center text-sm text-muted-foreground">
      {{ tr('No ads waiting for manual review') }}
    </p>

    <div v-else-if="compact" class="flex flex-col gap-2">
      <div
        v-for="row in items"
        :key="'waiting-list--' + row.id"
        class="glass-tile flex cursor-pointer items-center gap-3 rounded-xl p-3 transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]"
        @click="() => openItem(row)"
      >
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <span class="flex min-w-0 items-center gap-2 text-xs font-semibold text-muted-foreground">
            <rule-type-badge :action="row.rule.action" compact />
            <span class="min-w-0 flex-1 truncate">{{ row.rule.name }}</span>
            <rule-waiting-list-status :rule="row.rule" :entity="row" compact />
          </span>
          <inline-social-content :entity="row.ad ?? row.post" class="min-w-0" />
        </div>
        <arrow-link :label="tr('Details')" class="max-sm:hidden" @click.stop="() => openItem(row)" />
      </div>

      <template v-if="isLoading">
        <div v-for="i in 3" :key="'skeleton--' + i" class="glass-tile flex items-center gap-3 rounded-xl p-3">
          <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <span class="flex h-6 items-center gap-2">
              <ui-skeleton class="size-6 rounded-md" />
              <ui-skeleton class="h-3 w-40" />
            </span>
            <inline-social-content />
          </div>
          <ui-skeleton class="size-8 rounded-full max-sm:hidden" />
        </div>
      </template>
    </div>

    <empty-screen
      v-else-if="!isLoading && isListEmpty"
      icon="clock-sleepy"
      :title="tr('No ads waiting for manual review')"
      :flag-text="tr('Full control over your ads')"
      :description="tr('When you enable manual review in your automation settings, all new ads will appear here before going live. This safety feature gives you complete control over which automated campaigns can start running on your account, letting you review and approve each campaign before spending begins.')"
    />

    <div v-else class="flex flex-col gap-2.5">
      <div class="grid gap-4 md:grid-cols-[13rem_minmax(0,1fr)_14rem_2rem] md:items-center px-4 text-xs font-semibold text-muted-foreground max-md:hidden">
        <span>{{ tr('Source') }}</span>
        <span>{{ contentLabel }}</span>
        <span>{{ tr('Status') }}</span>
        <span />
      </div>

      <div
        v-for="row in items"
        :key="'waiting-list--' + row.id"
        :class="[ 'glass-tile flex flex-col overflow-hidden rounded-xl', GLOW_TRANSITION, ruleActionStyle(row.rule.action).glow ]"
      >
        <!-- The automation in a strip of its type's color, as on the activity log cards. -->
        <router-link
          v-if="!isRuleSpecific"
          :to="'/automation/' + row.rule.id + '/overview'"
          :class="[ 'flex min-w-0 items-center gap-2 px-4 py-1.5 text-xs font-semibold text-white no-underline hover:underline', ruleActionStyle(row.rule.action).bg ]"
        >
          <i :class="[ 'icon white size-3.5 shrink-0', ruleActionStyle(row.rule.action).icon ]" />
          <span class="truncate">{{ row.rule.name }}</span>
        </router-link>

        <div class="grid gap-4 p-4 md:grid-cols-[13rem_minmax(0,1fr)_14rem_2rem] md:items-center">
          <social-page :source-profile="row.post.source_profile" :date="row.created_at" />
          <inline-social-content
            :entity="row.ad ?? row.post"
            :badge="row.affected_ad_count > 1 ? tr('Total :count ad variants', { ':count': row.affected_ad_count }) : null"
            class="min-w-0"
          />
          <rule-waiting-list-status
            :rule="row.rule"
            :entity="row"
            :disabled="loadingButton !== null || $root.isSupportMode"
            @loading-button="(value) => loadingButton = value"
          />
          <div class="flex items-center justify-between gap-2 md:justify-end">
            <div class="flex flex-col md:hidden">
              <router-link :to="'/posts/' + row.post.id" class="text-sm font-semibold text-primary no-underline">
                {{ tr('Go to Post') }}
              </router-link>
              <router-link :to="'/automation/' + row.rule.id" class="text-sm font-semibold text-primary no-underline">
                {{ tr('Go to Automation') }}
              </router-link>
            </div>
            <arrow-link :label="tr('Details')" @click="() => openItem(row)" />
          </div>
        </div>
      </div>

      <template v-if="(hasMore && !disablePager) || isLoading">
        <div
          v-for="i in ($root.isMobile && !isListEmpty ? 1 : 3)"
          :key="'skeleton--' + i"
          ref="scrollPagerTrigger"
          class="glass-tile flex flex-col overflow-hidden rounded-xl"
        >
          <!-- The parts of a loaded card, with placeholders of the same size. -->
          <ui-skeleton v-if="!isRuleSpecific" class="h-7 rounded-none" />
          <div class="grid gap-4 p-4 md:grid-cols-[13rem_minmax(0,1fr)_14rem_2rem] md:items-center">
            <social-page />
            <inline-social-content />
            <div class="flex flex-col gap-0.5">
              <ui-skeleton class="h-9 w-full" />
              <span class="flex flex-col">
                <span class="flex h-4 items-center"><ui-skeleton class="h-3 w-40" /></span>
                <!-- The status text wraps to a second line in the narrow desktop column. -->
                <span class="hidden h-4 items-center md:flex"><ui-skeleton class="h-3 w-24" /></span>
              </span>
            </div>
            <div class="flex items-center justify-between gap-2 md:justify-end">
              <div class="flex flex-col md:hidden">
                <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-24" /></span>
                <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-32" /></span>
              </div>
              <ui-skeleton class="size-8 rounded-full" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Search } from '@lucide/vue';

import { RULE_ACTION, RULE_ACTIVITY_MANAGEMENT_INSTRUCTION } from '@/consts.js';
import RuleActivityManagementQueueItem from '@/entities/RuleActivityManagementQueueItem.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import RuleService from '@/services/Rule.js';
import RuleActivityManagementService from '@/services/RuleActivityManagement.js';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import RuleWaitingListItemModalContent from '@/ui/components/RuleWaitingListItemModalContent.vue';
import RuleWaitingListStatus from '@/ui/components/RuleWaitingListStatus.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';
import debounce from '@/utils/debounce.js';

const ITEM_PER_PAGE = 20;

export default {
  name: 'RuleWaitingListContent',
  components: {
    ArrowLink,
    BrandFilter,
    EmptyScreen,
    InlineSocialContent,
    RuleTypeBadge,
    RuleWaitingListStatus,
    SearchIcon: Search,
    SocialPage,
  },
  mixins: [ ScrollPagerMixin ],
  props: {
    rule: {
      type: Object,
      default: null,
    },
    isRuleSpecific: {
      type: Boolean,
      default: false,
    },
    numberOfItems: {
      type: Number,
      default: ITEM_PER_PAGE,
    },
    brandId: {
      type: [ String, Number, null ],
      default: null,
    },
    disablePager: {
      type: Boolean,
      default: false,
    },
    // One line per item with a status badge (dashboard); actions are in the item modal.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      GLOW_TRANSITION,
      isLoading: false,
      hasMore: true,
      page: 0,
      items: [],
      loadingButton: null,

      searchTerm: '',
      internalBrandId: this.brandId ? this.brandId : null,

      itemIdInQuery: null,
      doInQuery: null,
    };
  },
  computed: {
    ruleId() {
      return this.isRuleSpecific ? this.$route.params.id : null;
    },
    isListEmpty() {
      return this.items.length === 0;
    },
    contentLabel() {
      if (!this.isRuleSpecific) {
        return this.tr('Content');
      }

      if (this.rule) {
        switch (this.rule.action) {
          case RULE_ACTION.START:
            return this.tr('Post content');

          case RULE_ACTION.SCALING_UP:
          case RULE_ACTION.STOP:
            return this.tr('Ad content');
        }
      }

      return this.tr('Content');
    },

  },
  watch: {
    rule: {
      immediate: true,
      handler(val) {
        if (this.isRuleSpecific && val) {
          this.resetAndLoad();
        }
      },
    },
    isLoading(val) {
      if (!val && this.itemIdInQuery && this.doInQuery) {
        this.autoHandleByQueryParams(this.itemIdInQuery, this.doInQuery);

        this.itemIdInQuery = null;
        this.doInQuery = null;
      }
    },
    searchTerm(newVal, oldVal) {
      if (this.debouncedTermFilter && oldVal !== newVal) {
        this.debouncedTermFilter();
      }
    },
    internalBrandId: {
      immediate: true,
      handler() {
        this.resetAndLoad();
      },
    },
    brandId: {
      immediate: true,
      handler() {
        this.internalBrandId = this.brandId;
        this.resetAndLoad();
      },
    },
  },
  created() {
    this.itemIdInQuery = this.$route.query['item-id'];
    this.doInQuery = this.$route.query['do'];
    if (this.itemIdInQuery) {
      this.$router.replace(this.$route.path);
    }

    if (!this.isRuleSpecific) {
      this.loadNewData();
    }

    this.debouncedTermFilter = debounce(() => {
      this.resetAndLoad();
    }, 500);
  },
  methods: {
    ruleActionStyle,
    resetAndLoad() {
      this.items = [];
      this.page = 0;
      this.hasMore = true;
      this.loadNewData();
    },
    autoHandleByQueryParams(itemId, instruction) {
      let item = this.items.find(i => i.id === itemId);
      if (!item) {
        item = new RuleActivityManagementQueueItem({ id: itemId });
      }

      if (
        instruction !== RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT
        && instruction !== RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT
      ) {
        return;
      }

      const ruleId = item.rule?.id || this.ruleId;
      if (!ruleId) {
        this.error(new Error('Rule ID not found'));
        return;
      }

      this.loadingButton = itemId + '--' + instruction;

      RuleActivityManagementService.instruct(ruleId, item.id, instruction)
        .then(resp => {
          item.updateWith(resp);

          this.$toast.clear();
          if (instruction === RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT) {
            this.$toast.success(this.tr('Activity accepted successfully. The activity will run in a few minutes.'));
          }
          else if (instruction === RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT) {
            this.$toast.success(this.tr('Activity rejected successfully.'));
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.loadingButton = null);
    },
    loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      const params = {
        page: this.page,
        limit: this.numberOfItems,
        term: this.searchTerm,
        brandId: this.internalBrandId,
      };

      if (this.isRuleSpecific && this.ruleId) {
        params.ruleId = this.ruleId;
      }

      RuleService.getWaitingList(params)
        .then(resp => {
          this.items.push(...resp);
          this.hasMore = resp.length === this.numberOfItems;

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore && !this.disablePager) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    openItem(item) {
      this.$fullscreenModal.open(
        this,
        RuleWaitingListItemModalContent,
        {
          rule: item.rule,
          entity: item,
        },
        { closeWithOutclick: false },
      );
    },
  },
};
</script>
